import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star, Phone, MapPin, MessageCircle, Clock } from "lucide-react";
import api from "../lib/api";
import { useSiteSettings } from "../lib/hooks";
import VehicleCard from "../components/VehicleCard";
import { formatPriceINR, buildWhatsAppLink } from "../lib/format";

const HERO_IMG = "https://images.pexels.com/photos/28891882/pexels-photo-28891882.jpeg?auto=compress&cs=tinysrgb&w=2400";
const SHOWROOM_IMG = "https://images.pexels.com/photos/18108314/pexels-photo-18108314.jpeg";
const KEYS_IMG = "https://images.pexels.com/photos/16714035/pexels-photo-16714035.jpeg";
const STORY_IMG = "https://images.pexels.com/photos/210019/pexels-photo-210019.jpeg?auto=compress&cs=tinysrgb&w=2400";
const DETAIL_IMG = "https://images.pexels.com/photos/32255098/pexels-photo-32255098.jpeg?auto=compress&cs=tinysrgb&w=2400";
const TESTDRIVE_IMG = "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=2200&q=80";

const Hero = () => (
  <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0A0A0A]" data-testid="home-hero">
    <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover object-[60%_center] brightness-[1.05] contrast-[1.08] saturate-[1.1]" loading="eager" />
    {/* Left-weighted gradient keeps headline crisp while the bright showroom + vehicle stays vivid on the right. */}
    <div className="absolute inset-0" style={{background: "linear-gradient(to right, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.78) 34%, rgba(10,10,10,0.28) 58%, rgba(10,10,10,0) 78%)"}} />
    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
    <div className="relative z-10 w-full px-6 sm:px-10 lg:px-16 xl:px-20">
      <div className="max-w-xl fade-in-up">
        <div className="overline text-[#7A1519] mb-4 tracking-[0.28em] font-semibold">Adding</div>
        <h1 className="font-display text-white tracking-tight leading-[0.94] font-semibold">
          <span className="block text-6xl md:text-7xl lg:text-8xl">LUXURY</span>
          <span className="block text-6xl md:text-7xl lg:text-8xl text-[#7A1519]">TO LIFE</span>
        </h1>
        <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-md">
          Discover exceptional pre-owned cars that redefine every drive.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/cars" className="inline-flex items-center gap-3 bg-[#7A1519] hover:bg-[#5a0f12] text-white px-7 py-3.5 rounded-sm text-sm font-semibold tracking-wider uppercase transition-colors" data-testid="hero-cta-explore">
            Explore Collection <ArrowRight size={16} />
          </Link>
          <Link to="/sell-your-car" className="inline-flex items-center gap-3 border border-white/60 text-white hover:bg-white hover:text-[#111111] px-7 py-3.5 rounded-sm text-sm font-semibold tracking-wider uppercase transition-colors" data-testid="hero-cta-sell">
            Sell Your Car <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// Reusable cinematic image band — full-width photo with editorial overlay + CTA.
// The image gets a subtle parallax drift + gentle zoom; text fades/slides in
// when the section enters the viewport. Both effects are lightweight (no libs)
// and respect prefers-reduced-motion + are toned down on mobile via CSS.
const CinematicBand = ({ image, overline, title, cta, ctaHref, alignRight = false, testId }) => (
  <section className="ab-parallax-wrap relative w-full h-[70vh] md:h-[80vh] overflow-hidden ab-section-fade-top ab-section-fade-bottom" data-testid={testId} data-ab-reveal-root>
    <img src={image} alt="" className="ab-parallax absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className={`absolute inset-0 ${alignRight ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-black/85 via-black/50 to-transparent`} />
    <div className="container-wide relative z-10 h-full flex items-center">
      <div className={`max-w-xl ${alignRight ? "ml-auto text-right" : ""}`}>
        <div className="overline text-[#7A1519] mb-4 ab-reveal">{overline}</div>
        <h2 className="font-display text-white text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] font-semibold ab-reveal ab-reveal-slow ab-reveal-delay-1">
          {title}
        </h2>
        {cta && ctaHref && (
          <Link to={ctaHref} className="mt-8 inline-flex items-center gap-3 bg-[#7A1519] hover:bg-[#5a0f12] text-white px-7 py-3.5 rounded-sm text-sm font-semibold tracking-wide transition-colors ab-reveal ab-reveal-delay-2">
            {cta} <ArrowRight size={16} />
          </Link>
        )}
      </div>
    </div>
  </section>
);

const QuickSearch = ({ facets }) => {
  const [q, setQ] = useState({ brand: "", body_type: "", max_price: "" });
  const params = new URLSearchParams();
  if (q.brand) params.set("brand", q.brand);
  if (q.body_type) params.set("body_type", q.body_type);
  if (q.max_price) params.set("max_price", q.max_price);
  return (
    <section className="container-wide -mt-16 relative z-20" data-testid="quick-search">
      <div className="bg-white border hairline rounded-sm shadow-lg p-6 md:p-8 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="overline text-[#242424]/70 block mb-2">Brand</label>
          <select value={q.brand} onChange={(e) => setQ({ ...q, brand: e.target.value })} className="w-full bg-[#F7F6F3] px-4 py-3 rounded-sm text-sm border hairline focus:outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid="qs-brand">
            <option value="">Any brand</option>
            {facets?.brands?.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-[#242424]/70 block mb-2">Body Type</label>
          <select value={q.body_type} onChange={(e) => setQ({ ...q, body_type: e.target.value })} className="w-full bg-[#F7F6F3] px-4 py-3 rounded-sm text-sm border hairline focus:outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid="qs-body">
            <option value="">Any body</option>
            {facets?.body_types?.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-[#242424]/70 block mb-2">Budget (upto)</label>
          <select value={q.max_price} onChange={(e) => setQ({ ...q, max_price: e.target.value })} className="w-full bg-[#F7F6F3] px-4 py-3 rounded-sm text-sm border hairline focus:outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid="qs-budget">
            <option value="">Any budget</option>
            <option value="2000000">Under ₹20 L</option>
            <option value="3000000">Under ₹30 L</option>
            <option value="5000000">Under ₹50 L</option>
            <option value="7500000">Under ₹75 L</option>
            <option value="10000000">Under ₹1 Cr</option>
          </select>
        </div>
        <Link to={`/cars?${params.toString()}`} className="bg-[#7A1519] hover:bg-[#5a0f12] text-white px-6 py-3.5 rounded-sm text-sm font-semibold tracking-wide text-center transition-colors" data-testid="qs-submit">
          Find my Car
        </Link>
      </div>
    </section>
  );
};

const SectionHeader = ({ overline, title, right }) => (
  <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
    <div>
      <div className="overline text-[#7A1519] mb-3 ab-reveal">{overline}</div>
      <h2 className="font-display text-3xl md:text-5xl tracking-tighter text-[#111111] font-semibold leading-tight ab-reveal ab-reveal-delay-1">{title}</h2>
    </div>
    {right}
  </div>
);

const LatestArrivals = ({ vehicles }) => (
  <section className="container-wide py-24 md:py-32" data-testid="section-latest-arrivals">
    <SectionHeader
      overline="Latest Arrivals"
      title="Just rolled onto the floor"
      right={<Link to="/latest-arrivals" className="hidden md:inline-flex items-center gap-2 text-sm text-[#242424] hover-champagne">View all <ArrowRight size={14} /></Link>}
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {vehicles.slice(0, 8).map((v) => <VehicleCard key={v.id} vehicle={v} testIdPrefix="latest" />)}
    </div>
    <div className="md:hidden mt-8 text-center">
      <Link to="/latest-arrivals" className="text-sm underline text-[#7A1519]">View all latest arrivals</Link>
    </div>
  </section>
);

const FeaturedCars = ({ vehicles }) => {
  if (!vehicles.length) return null;
  return (
    <section className="bg-[#111111] text-white py-24 md:py-32" data-testid="section-featured">
      <div className="container-wide">
        <SectionHeader
          overline="Featured"
          title="Our current favourites"
          right={<Link to="/cars?featured=true" className="hidden md:inline-flex items-center gap-2 text-sm text-white/70 hover:text-white">View all <ArrowRight size={14} /></Link>}
        />
      </div>
      <div className="overflow-x-auto marquee-mask">
        <div className="flex gap-6 px-6 md:px-10 pb-4">
          {vehicles.map((v) => (
            <div key={v.id} className="min-w-[300px] sm:min-w-[360px] max-w-[380px]">
              <VehicleCard vehicle={v} testIdPrefix="featured" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BrowseByBrand = ({ brands }) => (
  <section className="container-wide py-24" data-testid="section-brands">
    <SectionHeader overline="By brand" title="Browse the showroom" />
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {brands.map((b) => (
        <Link
          key={b}
          to={`/cars?brand=${encodeURIComponent(b)}`}
          className="border hairline bg-white text-center py-6 px-3 rounded-sm text-sm font-medium transition-colors hover-champagne-border"
          data-testid={`brand-chip-${b.toLowerCase().replace(/\s+/g,'-')}`}
        >
          {b}
        </Link>
      ))}
    </div>
  </section>
);

const BrowseByBody = ({ types }) => (
  <section className="container-wide pb-24" data-testid="section-body-types">
    <SectionHeader overline="By body type" title="Find your shape" />
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {types.map((b) => (
        <Link
          key={b}
          to={`/cars?body_type=${encodeURIComponent(b)}`}
          className="border hairline bg-white py-8 px-4 rounded-sm font-display font-semibold tracking-tight transition-colors hover-champagne-border"
          data-testid={`body-chip-${b.toLowerCase()}`}
        >
          {b}
        </Link>
      ))}
    </div>
  </section>
);

const WhyUs = ({ items }) => (
  <section className="bg-white border-y hairline py-24 md:py-32" data-testid="section-why-us">
    <div className="container-wide">
      <SectionHeader overline="Why Autobahn Cars" title="A different kind of pre-owned." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
        {items?.map((it, i) => (
          <div key={i} className="border-t hairline pt-6" data-testid={`why-item-${i}`}>
            <div className="text-4xl font-display font-light text-[#7A1519]">{String(i + 1).padStart(2, "0")}</div>
            <h3 className="mt-4 font-display font-semibold text-lg tracking-tight text-[#111111]">{it.title}</h3>
            <p className="mt-2 text-sm text-[#242424]/75 leading-relaxed">{it.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SellCTA = () => (
  <section className="container-wide py-24 md:py-32" data-testid="section-sell-cta">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
      <div className="relative aspect-[4/3] overflow-hidden rounded-sm order-2 lg:order-1">
        <img src={KEYS_IMG} alt="Sell your car" className="w-full h-full object-cover" />
      </div>
      <div className="order-1 lg:order-2">
        <div className="overline text-[#7A1519] mb-4 ab-reveal">Sell Your Car</div>
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter text-[#111111] font-semibold leading-[0.95] ab-reveal ab-reveal-delay-1">
          EASY.<br/>FAST.<br/>HASSLE-FREE.
        </h2>
        <p className="mt-6 text-[#242424]/75 text-base md:text-lg leading-relaxed max-w-lg">
          Get the best value for your car with our quick and transparent evaluation process —
          a fair offer, quick paperwork and pickup handled for you.
        </p>
        <Link to="/sell-your-car" className="mt-8 inline-flex items-center gap-3 bg-[#7A1519] hover:bg-[#5a0f12] text-white px-8 py-4 rounded-sm text-sm font-semibold tracking-wide transition-colors" data-testid="sell-cta-btn">
          Get a Free Valuation <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

const Reviews = ({ reviews }) => (
  <section className="bg-[#F7F6F3] py-24 md:py-32" data-testid="section-reviews">
    <div className="container-wide">
      <SectionHeader
        overline="Reviews"
        title="What our customers say"
        right={<Link to="/reviews" className="hidden md:inline-flex items-center gap-2 text-sm text-[#242424] hover-champagne">Read more <ArrowRight size={14} /></Link>}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reviews.slice(0, 3).map((r) => (
          <div key={r.id} className="bg-white border hairline p-8 rounded-sm" data-testid={`review-${r.id}`}>
            <div className="flex gap-1 text-[#7A1519]">
              {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
            </div>
            <p className="mt-5 text-sm text-[#242424] leading-relaxed">"{r.text}"</p>
            <div className="mt-6 pt-4 border-t hairline">
              <div className="font-display font-semibold text-[#111111]">{r.customer_name}</div>
              {r.vehicle_purchased && <div className="text-xs text-[#242424]/60 mt-0.5">{r.vehicle_purchased}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const EMIPreview = () => (
  <section className="container-wide py-24" data-testid="section-emi">
    <div className="bg-[#111111] text-white rounded-sm overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 p-10 md:p-16 items-center">
        <div>
          <div className="overline text-[#7A1519] mb-4 ab-reveal">Finance</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-semibold leading-tight ab-reveal ab-reveal-delay-1">
            Plan your drive.<br />Estimate your EMI.
          </h2>
          <p className="mt-6 text-white/70 text-base leading-relaxed max-w-md">
            A quick estimate to help you plan. Actual finance terms depend on lender approval and eligibility.
          </p>
          <Link to="/emi-calculator" className="mt-8 inline-flex items-center gap-3 bg-white text-[#111111] hover:bg-[#F7F6F3] px-8 py-4 rounded-sm text-sm font-semibold tracking-wide transition-colors" data-testid="emi-preview-btn">
            Open EMI Calculator <ArrowRight size={16} />
          </Link>
        </div>
        <div className="border border-white/20 p-10 rounded-sm">
          <div className="overline text-white/50 mb-4">Sample</div>
          <div className="flex items-baseline justify-between text-white/80 text-sm py-2 border-b border-white/10">
            <span>Vehicle price</span><span>{formatPriceINR(4500000)}</span>
          </div>
          <div className="flex items-baseline justify-between text-white/80 text-sm py-2 border-b border-white/10">
            <span>Down payment</span><span>{formatPriceINR(500000)}</span>
          </div>
          <div className="flex items-baseline justify-between text-white/80 text-sm py-2 border-b border-white/10">
            <span>Rate · Tenure</span><span>9.5% · 60 mo</span>
          </div>
          <div className="mt-6 flex items-baseline justify-between">
            <div className="overline text-white/50">Estimated EMI</div>
            <div className="font-display text-3xl text-[#7A1519] font-semibold">₹ 83,984 / mo</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ContactStrip = ({ settings }) => {
  const wa = settings?.whatsapp ? buildWhatsAppLink(settings.whatsapp, "Hi Autobahn Cars, I'd like to visit the showroom.") : null;
  return (
    <section className="container-wide pb-24" data-testid="section-contact-strip">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border hairline p-8 rounded-sm">
          <MapPin size={20} className="text-[#7A1519]" />
          <div className="overline text-[#242424]/60 mt-4">Showroom</div>
          <div className="mt-2 text-sm text-[#242424]">{settings?.address || "—"}</div>
          {settings?.google_maps_link && (
            <a href={settings.google_maps_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-[#7A1519] hover:underline">Get Directions <ArrowUpRight size={12} /></a>
          )}
        </div>
        <div className="bg-white border hairline p-8 rounded-sm">
          <Clock size={20} className="text-[#7A1519]" />
          <div className="overline text-[#242424]/60 mt-4">Hours</div>
          <div className="mt-2 text-sm text-[#242424] whitespace-pre-line">{settings?.opening_hours}</div>
        </div>
        <div className="bg-white border hairline p-8 rounded-sm">
          <Phone size={20} className="text-[#7A1519]" />
          <div className="overline text-[#242424]/60 mt-4">Get in touch</div>
          <div className="mt-2 text-sm text-[#242424]">{settings?.phone}</div>
          <div className="mt-3 flex gap-3">
            <a href={`tel:${settings?.phone}`} className="text-xs bg-[#7A1519] text-white px-3 py-1.5 rounded-sm">Call</a>
            {wa && <a href={wa} target="_blank" rel="noreferrer" className="text-xs border hairline px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><MessageCircle size={12} className="text-[#25D366]"/>WhatsApp</a>}
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const settings = useSiteSettings();
  const [latest, setLatest] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [facets, setFacets] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/vehicles?limit=8").then((r) => setLatest(r.data)).catch(() => {});
    api.get("/vehicles?featured=true&limit=6").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/vehicles/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/reviews?featured_only=true&limit=3").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  // Cinematic scroll enhancements — reveal-on-enter + subtle parallax on bands.
  // Lightweight: single IntersectionObserver + rAF-throttled scroll handler.
  // Respects prefers-reduced-motion and skips parallax on small screens.
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    // 1. Reveal-on-enter for any element carrying .ab-reveal.
    const revealEls = document.querySelectorAll(".ab-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    revealEls.forEach((el) => io.observe(el));

    // 2. Section-level visibility for the parallax wrappers (kicks the zoom).
    const wraps = document.querySelectorAll(".ab-parallax-wrap");
    const io2 = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.target.classList.toggle("is-visible", e.isIntersecting)),
      { threshold: 0.05 }
    );
    wraps.forEach((el) => io2.observe(el));

    // 3. Parallax — translate each band's inner image against page scroll.
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    let rafId = null;
    const updateParallax = () => {
      rafId = null;
      if (isMobile) return; // CSS already neutralises transforms on mobile
      const vh = window.innerHeight;
      wraps.forEach((wrap) => {
        const rect = wrap.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > vh + 100) return; // off-screen
        // -1..+1 progress across viewport; multiply by max travel (~60px).
        const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        const py = Math.max(-60, Math.min(60, -progress * 60));
        const img = wrap.querySelector(".ab-parallax");
        if (img) img.style.setProperty("--py", `${py}px`);
      });
    };
    const onScroll = () => {
      if (rafId == null) rafId = window.requestAnimationFrame(updateParallax);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    updateParallax();

    return () => {
      io.disconnect();
      io2.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [latest, featured, facets, reviews]);

  return (
    <div data-testid="page-home">
      <Hero />
      <QuickSearch facets={facets} />
      <LatestArrivals vehicles={latest} />
      <CinematicBand
        image={STORY_IMG}
        overline="Crafted for performance"
        title={<><span className="block">EVERY DRIVE</span><span className="block">TELLS A STORY</span></>}
        cta="Explore Collection"
        ctaHref="/cars"
        testId="section-cinematic-story"
      />
      <FeaturedCars vehicles={featured} />
      <BrowseByBrand brands={facets?.brands || []} />
      <CinematicBand
        image={DETAIL_IMG}
        overline="The details matter"
        title={<><span className="block">LUXURY</span><span className="block">IS IN THE</span><span className="block">DETAILS.</span></>}
        alignRight
        testId="section-cinematic-detail"
      />
      <BrowseByBody types={facets?.body_types || []} />
      <WhyUs items={settings?.why_items || []} />
      <SellCTA />
      <Reviews reviews={reviews} />
      <EMIPreview />
      <CinematicBand
        image={TESTDRIVE_IMG}
        overline="Test drive"
        title={<><span className="block">READY TO EXPERIENCE</span><span className="block">THE AUTOBAHN</span><span className="block">DIFFERENCE?</span></>}
        cta="Book a Test Drive"
        ctaHref="/test-drive"
        testId="section-cinematic-testdrive"
      />
      <ContactStrip settings={settings} />
    </div>
  );
};

export default Home;
